import * as React from 'react'
import { View, Text, ScrollView } from 'react-native'
import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import SectionTitle from '@components/sectionTitle/SectionTitle'
import Icon from '@components/icon/Icons'
import SimpleAlert from '@components/modal/SimpleAlert'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as AdminStore from '@store/admin/admin'
import * as SiteStore from '@store/site/site'
import * as AuthStore from '@store/auth/auth'
import { AuthScope } from '@api/enum'

import api from '@api/api'
import Logger from '@utils/logger'
import Button from '@components/button/Button'

import { TypeUtilisateur } from '@api/enum'

interface Props {
  userId: string
}

const MAIN_VERTICAL_PADDING = 24

const renderField = (label: string, value?: string, disabled?: boolean) => (
  <FieldContainer disabled={disabled}>
    <FieldLabel>{label}</FieldLabel>
    <FieldValue disabled={disabled}>{value || ' '}</FieldValue>
  </FieldContainer>
)

const renderItem = (label: string) => (
  <ItemContainer key={label}>
    <IconContainer>
      <Icon name="checkbox_checked" color={Theme.colors.primary} size={24} />
    </IconContainer>
    <ItemValue>{label}</ItemValue>
  </ItemContainer>
)

const AdminUserScreen = ({ userId }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const users = useReducer(AdminStore.store, (s) => s.users)
  const manifestations = useReducer(AdminStore.store, (s) => s.manifestations)
  const roles = useReducer(AdminStore.store, (s) => s.roles)
  const services = useReducer(AdminStore.store, (s) => s.services)
  const sites = useReducer(SiteStore.store, (s) => s.sites)

  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const [author, setAuthor] = React.useState<UserBasicResponse | UserResponse>()
  const [user, setUser] = React.useState<UserResponse>()

  const [status, setStatus] = React.useState<Status>('loading')
  const [activating, setActivating] = React.useState(false)

  const userRoles = React.useMemo(() => roles.filter((r) => !!user && user.roleIds.includes(r.id)), [user, roles])

  const userManifs = React.useMemo(
    () => manifestations.filter((m) => !!user && user.manifestationIds.includes(m.id)),
    [user, manifestations]
  )

  const canEdit =
    !!user &&
    user.typeUtilisateur !== TypeUtilisateur.EXPOSANT &&
    (scopes.includes(AuthScope.AdminUtilisateursCreationTous) ||
      (user.typeUtilisateur === TypeUtilisateur.PRESTATAIRE &&
        scopes.includes(AuthScope.AdminUtilisateursCreationPrestataire)) ||
      (user.typeUtilisateur === TypeUtilisateur.ORGANISATEUR &&
        scopes.includes(AuthScope.AdminUtilisateursCreationOrganisateur)))

  React.useEffect(() => {
    if (!userId) {
      setStatus('error')
    } else {
      api.users
        .all()
        .then((us) => {
          AdminStore.actions.setUsers(us)
          setStatus('fetched')
        })
        .catch((err) => {
          Logger.error(err)
          setStatus('error')
        })

      Promise.all([api.manifestation.all(), api.roles.all(), api.societe.all()])
        .then(([manifRes, rolRes, servRes]) => {
          AdminStore.actions.setManifestations(manifRes)
          AdminStore.actions.setRoles(rolRes)
          AdminStore.actions.setServices(servRes)
        })
        .catch(Logger.error)
    }
  }, [userId])

  React.useEffect(() => {
    setUser(users.find((u) => u.id === userId))
  }, [users, userId])

  React.useEffect(() => {
    if (user && user.modificationUser) {
      const found = users.find((u) => u.id === user.modificationUser)

      if (found) {
        setAuthor(found)
      } else {
        api.users.get(user.modificationUser).then(setAuthor).catch(Logger.error)
      }
    }
  }, [user])

  const errorAlert = () =>
    SimpleAlert.open({
      title: i18n.t('common.errorTitle'),
      description: i18n.t('common.error'),
      buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
    })

  const toggleEnabled = () => {
    SimpleAlert.close()

    if (!user) {
      errorAlert()
    } else {
      setActivating(true)

      api.users
        .update(userId, { ...user, langage: user.langage as Langage, enabled: !user.enabled })
        .then(setUser)
        .catch((err) => {
          Logger.error(err)
          errorAlert()
        })
        .finally(() => setActivating(false))
    }
  }

  const onDeactivate = () => {
    if (user) {
      SimpleAlert.open({
        description: i18n.t(`screens.admin.user.${user.enabled ? 'deactivate' : 'reactivate'}Description`, {
          userName: `${user.prenom} ${user.nom}`,
        }),
        buttons: [
          { label: i18n.t('common.cancel'), onClick: () => SimpleAlert.close(), color: 'light', disabled: activating },
          {
            label: i18n.t(`screens.admin.user.buttons.${user?.enabled ? 'deactivate' : 'reactivate'}`),
            onClick: toggleEnabled,
            disabled: activating,
          },
        ],
      })
    } else {
      errorAlert()
    }
  }

  const onDuplicate = () => {
    navigation.push(`/admin/users/form/start?id=${userId}`)
  }

  const deactivateButton = {
    label: i18n.t(`screens.admin.user.buttons.${user?.enabled ? 'deactivate' : 'reactivate'}`),
    onClick: onDeactivate,
    color: 'light' as ButtonColor,
    status: (activating ? 'loading' : 'active') as ButtonStatus,
  }

  const duplicateButton = {
    label: i18n.t('screens.admin.user.buttons.duplicate'),
    onClick: onDuplicate,
    status: 'active' as ButtonStatus,
  }

  const headerButtons = canEdit ? [deactivateButton, duplicateButton] : []

  const headerLinks: HeaderLink[] = [
    { label: i18n.t('screens.admin.users.title'), onClick: () => navigation.push('/admin/users') },
  ]

  const societe = user && user.societeId ? services.find((s) => s.id === user.societeId) : undefined

  const renderRole = (role: RoleResponse) => renderItem(role.name || i18n.t('screens.admin.user.roles.unknown'))

  const renderManifestation = (manifestation: ManifestationResponse) =>
    renderItem(manifestation.libelle || i18n.t('screens.admin.user.manifestation.unknown'))

  const renderSite = (siteId: string) => {
    const site = sites.find((s) => s.id === siteId)

    if (site) {
      return renderItem(i18n.t(`screens.site.sites.${site.codeLieu}`))
    } else {
      return renderItem(i18n.t('screens.admin.user.sites.unknown'))
    }
  }

  const lastUpdate = user
    ? i18n.t('screens.admin.user.lastUpdate', {
        date: new Date(user.creationDate),
        author: author ? `${author.prenom} ${author.nom}` : i18n.t('common.unknownUser'),
      })
    : ''

  const displayManif = !!user && user.typeUtilisateur === TypeUtilisateur.ORGANISATEUR
  const displayEditSite = !!user && user.typeUtilisateur !== TypeUtilisateur.ORGANISATEUR

  const prestaOption =
    !!user && user.typeUtilisateur === TypeUtilisateur.PRESTATAIRE ? `&societeId=${user.societeId}` : ''

  const manifOption =
    !!user && user.typeUtilisateur === TypeUtilisateur.ORGANISATEUR
      ? `&manifestationId=${user.manifestationIds[0]}`
      : ''

  return (
    <Root
      loading={status === 'loading'}
      header={isPhone ? (user && `${user.prenom} ${user.nom}`) || i18n.t('screens.admin.user.title') : undefined}
      fixed
      onLeft={navigation.goBack}>
      {!!user && status === 'fetched' ? (
        <>
          {!isPhone && (
            <HeaderContainer>
              <Header
                title={`${user.prenom} ${user.nom}`}
                description={lastUpdate}
                buttons={headerButtons}
                links={headerLinks}
              />
            </HeaderContainer>
          )}

          <ScrollContainer scrollIndicatorInsets={{ right: 1 }} bounces={false} keyboardShouldPersistTaps="handled">
            {isPhone && <LastUpdate>{lastUpdate}</LastUpdate>}
            <MainContainer>
              <SectionTitle
                title={i18n.t('screens.admin.user.infos.title')}
                button={
                  canEdit
                    ? {
                        label: i18n.t('common.modify'),
                        onClick: () =>
                          navigation.push(`/admin/users/form/info?id=${userId}&edit=info${prestaOption}${manifOption}`),
                      }
                    : undefined
                }
              />

              <Row>
                {renderField(i18n.t('screens.admin.user.infos.prenom'), user.prenom)}
                {renderField(i18n.t('screens.admin.user.infos.nom'), user.nom)}
                {renderField(i18n.t('screens.admin.user.infos.trigramme'), user.codeMagic)}
                {renderField(i18n.t('screens.admin.user.infos.fonction'), user.fonction)}
                {renderField(i18n.t('screens.admin.user.infos.typeUtilisateur'), user.typeUtilisateur)}
                {renderField(
                  i18n.t('screens.admin.user.infos.societe'),
                  societe?.nameSociete,
                  user.typeUtilisateur !== 'PRESTATAIRE'
                )}
                {renderField(i18n.t('screens.admin.user.infos.email'), user.email)}
                {renderField(i18n.t('screens.admin.user.infos.numeroTelephonePortable'), user.numeroTelephonePortable)}
              </Row>

              <SectionTitle
                title={i18n.t('screens.admin.user.roles.title')}
                button={
                  canEdit
                    ? {
                        label: i18n.t('common.modify'),
                        onClick: () => navigation.push(`/admin/users/form/scope?id=${userId}&edit=scope`),
                      }
                    : undefined
                }
              />
              {userRoles.length > 0 ? (
                <Row>{userRoles.map(renderRole)}</Row>
              ) : (
                <MissingInfo>{i18n.t('screens.admin.user.roles.none')}</MissingInfo>
              )}

              {displayManif && (
                <>
                  <SectionTitle
                    title={i18n.t('screens.admin.user.manifestations.title')}
                    button={
                      canEdit
                        ? {
                            label: i18n.t('common.modify'),
                            onClick: () => navigation.push(`/admin/users/form/manif?id=${userId}&edit=manif`),
                          }
                        : undefined
                    }
                  />
                  {userManifs.length > 0 ? (
                    <Row>{userManifs.map(renderManifestation)}</Row>
                  ) : (
                    <MissingInfo>{i18n.t('screens.admin.user.manifestations.none')}</MissingInfo>
                  )}
                </>
              )}

              <SectionTitle
                title={i18n.t('screens.admin.user.sites.title')}
                button={
                  canEdit && displayEditSite
                    ? {
                        label: i18n.t('common.modify'),
                        onClick: () => {
                          const step = user.typeUtilisateur === 'VIPARIS' ? 'site_viparis' : 'site'
                          navigation.push(`/admin/users/form/${step}?id=${userId}&edit=${step}`)
                        },
                      }
                    : undefined
                }
              />
              {user.siteIds.length > 0 ? (
                <Row>{user.siteIds.map(renderSite)}</Row>
              ) : (
                <MissingInfo>{i18n.t('screens.admin.user.sites.none')}</MissingInfo>
              )}
            </MainContainer>
          </ScrollContainer>

          {isPhone && (
            <BottomContainer>
              <ButtonContainer isLeft>
                <Button {...deactivateButton} />
              </ButtonContainer>
              <ButtonContainer>
                <Button {...duplicateButton} />
              </ButtonContainer>
            </BottomContainer>
          )}
        </>
      ) : (
        <MainContainer>
          <MissingInfo>{i18n.t('common.error')}</MissingInfo>
        </MainContainer>
      )}
    </Root>
  )
}

export default AdminUserScreen

const HeaderContainer = styled(View)`
  z-index: 10;
`

const ScrollContainer = styled(ScrollView)`
  flex: 1;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0;`}
`

const MainContainer = styled(View)`
  display: flex;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `${MAIN_VERTICAL_PADDING}px 16px` : '24px')};
`

const BottomContainer = styled(View)`
  padding: 16px;
  flex-direction: row;
  background-color: ${(props) => props.theme.colors.background};
`

const ButtonContainer = styled(View)<{ isLeft?: boolean }>`
  flex: 1;
  ${(props) => (props.isLeft ? 'margin-right: 8px' : 'margin-left: 8px')};
`

const Row = styled(View)`
  flex-flow: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')} wrap;
`

const FieldContainer = styled(View)<{ disabled?: boolean }>`
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `
        margin-bottom: 8px;
        padding: 4px 16px;
        background-color: ${props.theme.colors.background};
        border-bottom-width: 1px;
        border-bottom-color: ${props.theme.colors.primaryFocused};
        ${props.disabled && `background-color: ${props.theme.colors.secondaryActive}; opacity: 0.5;`}
      `
      : `
        width: 350px;
        margin-right: 16px;
        margin-bottom: 16px;`}
`

const FieldLabel = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Footnote : props.theme.fonts.Headline)};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `
        color: ${props.theme.colors.secondaryText};
        margin-bottom: 4px;`
      : `
        margin-bottom: 8px;`}
`

const FieldValue = styled(Text)<{ disabled?: boolean }>`
  ${(props) => props.theme.fonts.Body};
  ${(props) =>
    props.theme.breakpoint !== 'phone' &&
    `
      padding: 14px 16px;
      border-radius: 6px;
      border: 1px solid ${props.theme.colors.secondaryActive};`}

  ${(props) =>
    props.theme.breakpoint !== 'phone' &&
    !!props.disabled &&
    `
      background-color: ${props.theme.colors.secondaryActive};
      opacity: 0.5;`}
`

const ItemContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  width: 350px;
  margin-right: 16px;
  margin-bottom: 16px;
`

const ItemValue = styled(Text)`
  padding: 8px 16px;
  text-align: left;
  ${(props) => props.theme.fonts.Body};
`

const IconContainer = styled(View)`
  margin-right: 12px;
`

const LastUpdate = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.secondaryText};
  padding: 16px 16px 0;
`
